import {
  Field,
  type FieldRootProps,
  PinInput,
  type PinInputRootProps,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import { Caption } from "../caption/Caption.js";

interface OtpInputProps extends PinInputRootProps {
  label?: string;
  length?: number;
  size?: "sm" | "md" | "lg";
  helperText?: HelperTextProps;
  fieldProps?: FieldRootProps;
  asterisk?: boolean;
  classNames?: {
    root?: string;
    label?: string;
    control?: string;
    helperText?: string;
    input?: string;
    timer?: string;
  };
}

interface WithoutTimerProps extends OtpInputProps {
  withTimer?: false;
}

interface WithTimerProps extends OtpInputProps {
  withTimer?: true;
  endingTimer?: number;
  onResend?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  maxRetries?: number;
}

type OtpInputComponentProps = WithTimerProps | WithoutTimerProps;

const OtpInput = React.forwardRef<
  React.ElementRef<typeof PinInput.HiddenInput>,
  OtpInputComponentProps
>((props, ref) => {
  const {
    label,
    length = 4,
    placeholder = "",
    size = "md",
    fieldProps,
    helperText,
    invalid,
    asterisk = false,
    classNames,
    withTimer = false,
    ...rootProps
  } = props;

  const {
    onResend = () => {},
    endingTimer = 30,
    maxRetries = 3,
    ...filteredProps
  } = withTimer ? (rootProps as WithTimerProps) : {};

  const effectiveProps = withTimer ? filteredProps : rootProps;

  const [counter, setCounter] = React.useState(endingTimer ?? 30);
  const [isRetry, setIsRetry] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);

  React.useEffect(() => {
    if (counter > 0) {
      const timerId = setTimeout(() => setCounter(counter - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [counter]);

  const handleResend = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onResend) {
        onResend(event);
      }
      setRetryCount((prevCount) => prevCount + 1);
      setIsRetry(true);
      setCounter(endingTimer ?? 30);
      setTimeout(() => setIsRetry(false), 1000);
    },
    [onResend],
  );

  return (
    <Field.Root {...fieldProps}>
      <PinInput.Root
        className={clsx("ox-reset ox-OtpInputRoot", classNames?.root)}
        placeholder={placeholder}
        data-size={size}
        {...effectiveProps}
      >
        {label ? (
          <PinInput.Label
            data-size={size}
            className={clsx("ox-reset ox-FormLabel", classNames?.label)}
          >
            {label}
            {asterisk ? (
              <span className={clsx("ox-reset ox-FormLabelAsterisk")}>*</span>
            ) : null}
          </PinInput.Label>
        ) : null}
        <PinInput.Control
          data-size={size}
          className={clsx("ox-reset ox-OtpInputControl", classNames?.control)}
        >
          {[...Array(length).keys()].map((item, index) => (
            <PinInput.Input
              data-size={size}
              className={clsx("ox-reset ox-OtpInputInput", classNames?.input)}
              key={item}
              index={index}
            />
          ))}
        </PinInput.Control>
        <PinInput.HiddenInput ref={ref} />
        {helperText?.message ? (
          <Field.HelperText
            data-size={size}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
            data-invalid={invalid}
          >
            {getFormHelperText(helperText, size)}
          </Field.HelperText>
        ) : null}
        {withTimer ? (
          <div className={clsx("ox-reset ox-OtpFieldTimer", classNames?.timer)}>
            {counter > 0 ? (
              <Caption
                size="lg"
                className={clsx(
                  "ox-reset ox-OtpFieldTimerText",
                  classNames?.timer,
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm64-88a8,8,0,0,1-8,8H128a8,8,0,0,1-8-8V72a8,8,0,0,1,16,0v48h48A8,8,0,0,1,192,128Z"></path>
                </svg>{" "}
                Resend OTP in {counter} seconds
              </Caption>
            ) : !isRetry && retryCount <= (maxRetries ?? 3) ? (
              <button
                className={clsx("ox-reset ox-ResendOtpButton")}
                onClick={handleResend}
              >
                Resend OTP
              </button>
            ) : null}
          </div>
        ) : null}
      </PinInput.Root>
    </Field.Root>
  );
});
OtpInput.displayName = "OtpInput";
export { OtpInput };
export type { OtpInputProps };
